/**
 * This async file lives here since it needs to access the generated query, and stricter only allows one level of imports in UI
 */

import React, { useEffect, useCallback } from 'react';
import { lazy } from 'react-loosely-lazy';
import { useQueryLoader } from 'react-relay';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/index.tsx';
import Placeholder from '@atlassian/jira-placeholder';
import { createAri } from '@atlassian/jira-platform-ari';
import splitIssueModalQuery, {
	type splitIssueModalQuery as splitIssueModalQueryType,
} from '@atlassian/jira-relay/src/__generated__/splitIssueModalQuery.graphql';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import { usePathParam } from '@atlassian/react-resource-router';
import { useUFOComponentExperience } from '@atlassian/ufo';
import { APP_IDENTIFIER, EXPERIENCES, PACKAGE_NAME, SOURCE_NAME } from '../../common/constants';
import { initialLoadSplitIssueModalExperience } from '../../experiences';
import { useExperienceTracker } from '../../services/experience-tracker';
import type { ExtraErrorAttributes, Props } from '../../types';

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
const LazySplitIssueModal = lazy(
	() => import(/* webpackChunkName: "async-jira-software-backlog-split-issue-ui" */ './index'),
);

/**
 * This component is here in the async file because we don't want it to be part of the main entry point, which is being lazy loaded here.
 * We want this to be independant from the main chunk so that this component can fire off the GraphQL query in parallel to the async chunk load.
 */
export const SplitIssueDataLoader = (props: Props) => {
	const { cloudId } = useTenantContext();
	const [boardId = null] = usePathParam('boardId');
	const [onViewExperienceStart, onViewExperienceSuccess, onViewExperienceFail] =
		useExperienceTracker(EXPERIENCES.viewApp, SOURCE_NAME);

	useUFOComponentExperience(initialLoadSplitIssueModalExperience);

	const [queryReference, loadQuery] =
		useQueryLoader<splitIssueModalQueryType>(splitIssueModalQuery);

	const boardARI = createAri({
		resourceOwner: 'jira-software',
		cloudId,
		resourceType: 'board',
		resourceId: String(boardId),
	});

	const cardARI = createAri({
		resourceOwner: 'jira-software',
		cloudId,
		resourceType: 'card',
		resourceId: String(props.card.id),
	});

	const onError = useCallback(
		(location: string, error: Error, errorAttributes: ExtraErrorAttributes) => {
			onViewExperienceFail(location, error, errorAttributes);
			initialLoadSplitIssueModalExperience.failure();
		},
		[onViewExperienceFail],
	);

	useEffect(
		() => () => {
			initialLoadSplitIssueModalExperience.abort();
		},
		[],
	);

	useEffect(() => {
		loadQuery(
			{
				boardId: boardARI,
				cardId: cardARI,
			},
			{
				fetchPolicy: 'store-or-network',
			},
		);
		onViewExperienceStart();
	}, [boardARI, cardARI, loadQuery, onViewExperienceStart]);

	return queryReference ? (
		<JSErrorBoundary
			id={APP_IDENTIFIER}
			packageName={PACKAGE_NAME}
			fallback="flag"
			teamName="a4t-pandora"
			onError={onError}
		>
			<Placeholder name="jira-software-backlog-split-issue-ui" fallback="Loading...">
				<LazySplitIssueModal
					queryReference={queryReference}
					{...props}
					onLoadSuccess={onViewExperienceSuccess}
				/>
			</Placeholder>
		</JSErrorBoundary>
	) : null;
};

export default SplitIssueDataLoader;
